import React, { useState } from 'react';
import { Award, BookOpen, Code, BarChart, Globe, Briefcase, CheckCircle, Star, TrendingUp, Users, Clock, Shield } from 'lucide-react';

const Certifications = () => {
  const [selectedDomain, setSelectedDomain] = useState('all');

  const certifications = [
    {
      title: "Full Stack Web Development",
      domain: "technology",
      level: "Intermediate to Advanced",
      duration: "6 Months",
      description: "Industry-recognized certification in MERN/MEAN stack development with real-world projects",
      modules: ["HTML/CSS/JavaScript", "React/Angular", "Node.js & Express", "MongoDB/SQL", "RESTful APIs", "Cloud Deployment"],
      projects: 5,
      placement: "Placement Assistance Included",
      companies: "Startups to MNCs"
    },
    {
      title: "Data Science & Machine Learning",
      domain: "technology",
      level: "Advanced",
      duration: "8 Months",
      description: "Comprehensive certification in data analytics, ML algorithms, and AI applications",
      modules: ["Python Programming", "Statistics & Probability", "Machine Learning", "Deep Learning", "NLP", "Big Data Analytics"],
      projects: 6,
      placement: "Career Support Available",
      companies: "Tech Giants & Analytics Firms"
    },
    {
      title: "Digital Marketing Professional",
      domain: "business",
      level: "Beginner to Advanced",
      duration: "4 Months",
      description: "Complete digital marketing certification covering SEO, SEM, social media, and analytics",
      modules: ["SEO & SEM", "Social Media Marketing", "Content Marketing", "Email Marketing", "Google Analytics", "Ad Campaigns"],
      projects: 4,
      placement: "Freelancing Guidance",
      companies: "Agencies & Brands"
    },
    {
      title: "Financial Modeling & Analysis",
      domain: "business",
      level: "Intermediate",
      duration: "3 Months",
      description: "Professional certification in financial analysis, modeling, and investment strategies",
      modules: ["Financial Statements", "Excel Modeling", "Valuation Techniques", "Investment Analysis", "Portfolio Management", "Risk Assessment"],
      projects: 3,
      placement: "Industry Connections",
      companies: "Banks & Finance Firms"
    },
    {
      title: "Graphic Design & UI/UX",
      domain: "creative",
      level: "Beginner to Intermediate",
      duration: "5 Months",
      description: "Creative certification covering design principles, tools, and user experience",
      modules: ["Design Fundamentals", "Adobe Creative Suite", "Figma & Sketch", "UI/UX Principles", "Prototyping", "Portfolio Building"],
      projects: 8,
      placement: "Freelance Opportunities",
      companies: "Design Studios & Startups"
    },
    {
      title: "Content Writing & Copywriting",
      domain: "creative",
      level: "Beginner to Advanced",
      duration: "3 Months",
      description: "Professional writing certification for blogs, copy, technical, and creative content",
      modules: ["Writing Fundamentals", "SEO Writing", "Copywriting", "Technical Writing", "Storytelling", "Portfolio Development"],
      projects: 10,
      placement: "Freelancing Support",
      companies: "Content Agencies & Businesses"
    },
    {
      title: "Cloud Computing (AWS/Azure)",
      domain: "technology",
      level: "Intermediate to Advanced",
      duration: "4 Months",
      description: "Industry certification in cloud platforms, architecture, and DevOps practices",
      modules: ["Cloud Fundamentals", "AWS/Azure Services", "Cloud Architecture", "DevOps Practices", "Security & Compliance", "Cost Optimization"],
      projects: 4,
      placement: "High Demand Roles",
      companies: "Cloud-Native Companies"
    },
    {
      title: "Cybersecurity Specialist",
      domain: "technology",
      level: "Advanced",
      duration: "6 Months",
      description: "Professional certification in ethical hacking, security analysis, and threat management",
      modules: ["Network Security", "Ethical Hacking", "Penetration Testing", "Security Tools", "Incident Response", "Compliance"],
      projects: 5,
      placement: "Security Roles",
      companies: "Cybersecurity Firms"
    },
    {
      title: "Business Analytics",
      domain: "business",
      level: "Intermediate",
      duration: "4 Months",
      description: "Data-driven decision making certification with tools like Excel, Tableau, and Power BI",
      modules: ["Business Statistics", "Excel Advanced", "Tableau/Power BI", "SQL for Analytics", "Predictive Modeling", "Dashboard Creation"],
      projects: 4,
      placement: "Analyst Positions",
      companies: "Consulting & Corporates"
    },
    {
      title: "Project Management Professional",
      domain: "management",
      level: "Intermediate to Advanced",
      duration: "3 Months",
      description: "PMP-aligned certification in project planning, execution, and agile methodologies",
      modules: ["Project Planning", "Risk Management", "Agile & Scrum", "Stakeholder Management", "Budgeting", "Leadership"],
      projects: 2,
      placement: "Management Roles",
      companies: "All Industries"
    },
    {
      title: "Human Resource Management",
      domain: "management",
      level: "Beginner to Intermediate",
      duration: "4 Months",
      description: "Comprehensive HR certification covering recruitment, training, and organizational behavior",
      modules: ["Recruitment & Selection", "Training & Development", "Performance Management", "Compensation & Benefits", "Labor Laws", "HR Analytics"],
      projects: 3,
      placement: "HR Positions",
      companies: "Corporate HR Departments"
    },
    {
      title: "Video Editing & Production",
      domain: "creative",
      level: "Beginner to Advanced",
      duration: "4 Months",
      description: "Professional video production certification with industry-standard tools and techniques",
      modules: ["Video Fundamentals", "Adobe Premiere Pro", "After Effects", "Color Grading", "Audio Editing", "YouTube Production"],
      projects: 6,
      placement: "Content Creator Support",
      companies: "Media Houses & Digital"
    }
  ];

  const features = [
    {
      icon: Award,
      title: "Industry-Recognized",
      description: "Certifications valued by employers across industries with verified credentials"
    },
    {
      icon: Users,
      title: "Expert Instructors",
      description: "Learn from industry professionals with real-world experience and proven track records"
    },
    {
      icon: Briefcase,
      title: "Job Assistance",
      description: "Resume building, interview prep, and placement support for certified candidates"
    },
    {
      icon: Code,
      title: "Practical Projects",
      description: "Hands-on projects and real-world case studies to build impressive portfolios"
    }
  ];

  const benefits = [
    "Lifetime access to course materials and updates",
    "Live doubt-clearing sessions with experts",
    "Industry-relevant curriculum updated regularly",
    "Peer networking and community support",
    "Capstone project for portfolio building",
    "Certificate of completion from IIT alumni platform",
    "Career counseling and mentorship included",
    "Flexible learning - self-paced options available"
  ];

  const domains = [
    { value: 'all', label: 'All Certifications', icon: Globe },
    { value: 'technology', label: 'Technology', icon: Code },
    { value: 'business', label: 'Business', icon: BarChart },
    { value: 'creative', label: 'Creative', icon: Star },
    { value: 'management', label: 'Management', icon: Briefcase }
  ];

  const stats = [
    { number: "50+", label: "Certification Programs" },
    { number: "10,000+", label: "Certified Students" },
    { number: "85%", label: "Placement Rate" },
    { number: "200+", label: "Hiring Partners" }
  ];

  const filteredCertifications = selectedDomain === 'all' 
    ? certifications 
    : certifications.filter(c => c.domain === selectedDomain);

  return (
    <div style={{ fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif', backgroundColor: '#fff' }}>
      {/* Hero Section */}
      <section style={{
        background: 'linear-gradient(135deg, #000000 0%, #1a1a1a 100%)',
        color: 'white',
        padding: '100px 20px 80px',
        position: 'relative',
        overflow: 'hidden'
      }}>
        <div style={{
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: 'radial-gradient(circle at 60% 40%, rgba(88, 169, 91, 0.15) 0%, transparent 50%)',
          animation: 'pulse 4s ease-in-out infinite'
        }} />
        <div style={{ maxWidth: '1200px', margin: '0 auto', position: 'relative', zIndex: 1 }}>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))', gap: '40px', alignItems: 'center' }}>
            <div>
              <h1 style={{
                fontSize: 'clamp(2rem, 5vw, 3rem)',
                fontWeight: 'bold',
                marginBottom: '1.5rem',
                animation: 'fadeInUp 0.8s ease-out',
                lineHeight: '1.2'
              }}>
                Professional Certifications
                <span style={{ color: '#58A95B', display: 'block' }}>Boost Your Career</span>
              </h1>
              <p style={{
                fontSize: 'clamp(1rem, 2vw, 1.2rem)',
                marginBottom: '2rem',
                lineHeight: '1.8',
                animation: 'fadeInUp 1s ease-out'
              }}>
                Earn industry-recognized certifications in technology, business, creative arts, and management. 
                Gain practical skills, build impressive portfolios, and accelerate your career growth with 
                courses designed by IIT alumni and industry experts.
              </p>
              <button style={{
                backgroundColor: '#58A95B',
                border: 'none',
                padding: '15px 40px',
                fontSize: '1.1rem',
                fontWeight: '600',
                borderRadius: '8px',
                color: 'white',
                cursor: 'pointer',
                transition: 'all 0.3s ease',
                animation: 'fadeInUp 1.2s ease-out'
              }}
              onMouseOver={(e) => e.target.style.transform = 'translateY(-3px)'}
              onMouseOut={(e) => e.target.style.transform = 'translateY(0)'}>
                Explore Certifications
              </button>
            </div>
            <div style={{ textAlign: 'center', animation: 'float 3s ease-in-out infinite' }}>
              <Award size={200} color="#58A95B" strokeWidth={1.5} />
            </div>
          </div>
        </div>
      </section>

      {/* Stats */}
      <section style={{ padding: '60px 20px', backgroundColor: '#f8f9fa' }}>
        <div style={{ maxWidth: '1200px', margin: '0 auto' }}>
          <div style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
            gap: '30px',
            textAlign: 'center'
          }}>
            {stats.map((stat, idx) => (
              <div key={idx} style={{ animation: `fadeInUp ${0.5 + idx * 0.2}s ease-out` }}>
                <h2 style={{
                  fontSize: 'clamp(2.5rem, 5vw, 3.5rem)',
                  fontWeight: 'bold',
                  color: '#58A95B',
                  margin: '0 0 10px 0'
                }}>
                  {stat.number}
                </h2>
                <p style={{ fontSize: '1.1rem', color: '#666', margin: 0, fontWeight: '500' }}>
                  {stat.label}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Features */}
      <section style={{ padding: '80px 20px', backgroundColor: '#ffffff' }}>
        <div style={{ maxWidth: '1200px', margin: '0 auto' }}>
          <h2 style={{
            textAlign: 'center',
            fontSize: 'clamp(1.8rem, 4vw, 2.5rem)',
            fontWeight: 'bold',
            marginBottom: '3rem',
            color: '#000000'
          }}>
            Why Choose Our <span style={{ color: '#58A95B' }}>Certifications?</span>
          </h2>
          <div style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))',
            gap: '30px'
          }}>
            {features.map((feature, idx) => {
              const IconComponent = feature.icon;
              return (
                <div key={idx} style={{
                  backgroundColor: '#f8f9fa',
                  borderRadius: '15px',
                  padding: '30px 20px',
                  textAlign: 'center',
                  transition: 'all 0.3s ease',
                  cursor: 'pointer'
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.transform = 'translateY(-10px)';
                  e.currentTarget.style.backgroundColor = '#58A95B';
                  e.currentTarget.style.color = 'white';
                  const icon = e.currentTarget.querySelector('svg');
                  if (icon) icon.style.color = 'white';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.transform = 'translateY(0)';
                  e.currentTarget.style.backgroundColor = '#f8f9fa';
                  e.currentTarget.style.color = '#000';
                  const icon = e.currentTarget.querySelector('svg');
                  if (icon) icon.style.color = '#58A95B';
                }}>
                  <IconComponent size={50} color="#58A95B" style={{ marginBottom: '20px', transition: 'color 0.3s ease' }} />
                  <h5 style={{ fontWeight: '600', marginBottom: '10px' }}>{feature.title}</h5>
                  <p style={{ lineHeight: '1.6', margin: 0, opacity: 0.9 }}>{feature.description}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Certifications List */}
      <section style={{ padding: '80px 20px', backgroundColor: '#f8f9fa' }}>
        <div style={{ maxWidth: '1200px', margin: '0 auto' }}>
          <h2 style={{
            textAlign: 'center',
            fontSize: 'clamp(1.8rem, 4vw, 2.5rem)',
            fontWeight: 'bold',
            marginBottom: '2rem',
            color: '#000000'
          }}>
            Browse <span style={{ color: '#58A95B' }}>Certifications</span>
          </h2>

          {/* Domain Filter */}
          <div style={{
            display: 'flex',
            justifyContent: 'center',
            gap: '15px',
            marginBottom: '3rem',
            flexWrap: 'wrap'
          }}>
            {domains.map((domain) => {
              const IconComponent = domain.icon;
              return (
                <button
                  key={domain.value}
                  onClick={() => setSelectedDomain(domain.value)}
                  style={{
                    padding: '12px 25px',
                    borderRadius: '25px',
                    border: '2px solid',
                    borderColor: selectedDomain === domain.value ? '#58A95B' : '#ddd',
                    backgroundColor: selectedDomain === domain.value ? '#58A95B' : 'white',
                    color: selectedDomain === domain.value ? 'white' : '#333',
                    fontWeight: '600',
                    cursor: 'pointer',
                    transition: 'all 0.3s ease',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '8px'
                  }}
                  onMouseEnter={(e) => {
                    if (selectedDomain !== domain.value) {
                      e.currentTarget.style.borderColor = '#58A95B';
                      e.currentTarget.style.color = '#58A95B';
                    }
                  }}
                  onMouseLeave={(e) => {
                    if (selectedDomain !== domain.value) {
                      e.currentTarget.style.borderColor = '#ddd';
                      e.currentTarget.style.color = '#333';
                    }
                  }}
                >
                  <IconComponent size={18} />
                  {domain.label}
                </button>
              );
            })}
          </div>

          {/* Certifications Grid */}
          <div style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(320px, 1fr))',
            gap: '30px'
          }}>
            {filteredCertifications.map((cert, idx) => (
              <div key={idx} style={{
                backgroundColor: 'white',
                borderRadius: '15px',
                overflow: 'hidden',
                boxShadow: '0 5px 20px rgba(0,0,0,0.08)',
                transition: 'all 0.3s ease',
                display: 'flex',
                flexDirection: 'column'
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.transform = 'translateY(-8px)';
                e.currentTarget.style.boxShadow = '0 15px 40px rgba(88, 169, 91, 0.15)';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.transform = 'translateY(0)';
                e.currentTarget.style.boxShadow = '0 5px 20px rgba(0,0,0,0.08)';
              }}>
                <div style={{
                  background: 'linear-gradient(135deg, #58A95B 0%, #4a8f4d 100%)',
                  padding: '25px 20px',
                  color: 'white',
                  position: 'relative'
                }}>
                  <div style={{
                    position: 'absolute',
                    top: '15px',
                    right: '15px',
                    backgroundColor: 'rgba(0,0,0,0.3)',
                    padding: '5px 12px',
                    borderRadius: '20px',
                    fontSize: '0.75rem',
                    fontWeight: '600'
                  }}>
                    {cert.level}
                  </div>
                  <h4 style={{ fontWeight: 'bold', marginBottom: '8px', paddingRight: '120px' }}>{cert.title}</h4>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '15px', fontSize: '0.9rem', opacity: 0.95 }}>
                    <span style={{ display: 'flex', alignItems: 'center', gap: '5px' }}>
                      <Clock size={16} />
                      {cert.duration}
                    </span>
                    <span style={{ display: 'flex', alignItems: 'center', gap: '5px' }}>
                      <BookOpen size={16} />
                      {cert.projects} Projects
                    </span>
                  </div>
                </div>

                <div style={{ padding: '25px', flex: 1, display: 'flex', flexDirection: 'column' }}>
                  <p style={{ color: '#333', marginBottom: '20px', lineHeight: '1.6', fontSize: '0.95rem' }}>
                    {cert.description}
                  </p>

                  <div style={{ marginBottom: '20px', flex: 1 }}>
                    <h6 style={{ fontWeight: '600', marginBottom: '12px', color: '#000', fontSize: '0.95rem' }}>
                      Course Modules:
                    </h6>
                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '8px' }}>
                      {cert.modules.map((module, i) => (
                        <div key={i} style={{ display: 'flex', alignItems: 'center' }}>
                          <CheckCircle size={14} color="#58A95B" style={{ marginRight: '8px', flexShrink: 0 }} />
                          <span style={{ color: '#666', fontSize: '0.85rem' }}>{module}</span>
                        </div>
                      ))}
                    </div>
                  </div>

                  <div style={{
                    borderTop: '1px solid #eee',
                    paddingTop: '15px',
                    marginBottom: '15px',
                    display: 'flex',
                    flexDirection: 'column',
                    gap: '8px',
                    fontSize: '0.9rem'
                  }}>
                    <div style={{ display: 'flex', alignItems: 'center', color: '#666' }}>
                      <Briefcase size={16} style={{ marginRight: '8px', color: '#58A95B' }} />
                      <span>{cert.placement}</span>
                    </div>
                    <div style={{ display: 'flex', alignItems: 'center', color: '#666' }}>
                      <TrendingUp size={16} style={{ marginRight: '8px', color: '#58A95B' }} />
                      <span><strong>Hiring:</strong> {cert.companies}</span>
                    </div>
                  </div>

                  <button style={{
                    width: '100%',
                    backgroundColor: '#58A95B',
                    border: 'none',
                    padding: '12px',
                    fontWeight: '600',
                    borderRadius: '8px',
                    color: 'white',
                    cursor: 'pointer',
                    transition: 'all 0.3s ease'
                  }}
                  onMouseEnter={(e) => e.target.style.backgroundColor = '#4a8f4d'}
                  onMouseLeave={(e) => e.target.style.backgroundColor = '#58A95B'}>
                    Enroll Now
                  </button>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Benefits Section */}
      <section style={{ padding: '80px 20px', backgroundColor: '#000000', color: 'white' }}>
        <div style={{ maxWidth: '1200px', margin: '0 auto' }}>
          <h2 style={{
            textAlign: 'center',
            fontSize: 'clamp(1.8rem, 4vw, 2.5rem)',
            fontWeight: 'bold',
            marginBottom: '3rem',
            color:'wheat'
          }}>
            What's <span style={{ color: '#58A95B' }}>Included</span>
          </h2>
          <div style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))',
            gap: '20px'
          }}>
            {benefits.map((benefit, idx) => (
              <div key={idx} style={{
                backgroundColor: '#1a1a1a',
                padding: '20px',
                borderRadius: '10px',
                display: 'flex',
                alignItems: 'flex-start',
                gap: '15px',
                border: '1px solid #333',
                transition: 'all 0.3s ease'
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.borderColor = '#58A95B';
                e.currentTarget.style.transform = 'translateX(5px)';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.borderColor = '#333';
                e.currentTarget.style.transform = 'translateX(0)';
              }}>
                <CheckCircle size={20} color="#58A95B" style={{ flexShrink: 0, marginTop: '2px' }} />
                <span style={{ lineHeight: '1.6' }}>{benefit}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section style={{
        padding: '80px 20px',
        background: 'linear-gradient(135deg, #58A95B 0%, #4a8f4d 100%)',
        color: 'white',
        textAlign: 'center'
      }}>
        <div style={{ maxWidth: '800px', margin: '0 auto' }}>
          <h2 style={{ fontSize: 'clamp(1.8rem, 4vw, 2.5rem)', fontWeight: 'bold', marginBottom: '1.5rem' }}>
            Start Your Certification Journey Today
          </h2>
          <p style={{ fontSize: 'clamp(1rem, 2vw, 1.2rem)', marginBottom: '2rem', lineHeight: '1.6' }}>
            Invest in yourself and unlock new career opportunities. Get certified, build your portfolio, 
            and join thousands of successful professionals.
          </p>
          <div style={{ display: 'flex', gap: '20px', justifyContent: 'center', flexWrap: 'wrap' }}>
            <button style={{
              backgroundColor: '#000000',
              border: 'none',
              padding: '15px 40px',
              fontSize: '1.1rem',
              fontWeight: '600',
              borderRadius: '8px',
              color: 'white',
              cursor: 'pointer',
              transition: 'all 0.3s ease'
            }}
            onMouseOver={(e) => {
              e.target.style.backgroundColor = '#1a1a1a';
              e.target.style.transform = 'scale(1.05)';
            }}
            onMouseOut={(e) => {
              e.target.style.backgroundColor = '#000000';
              e.target.style.transform = 'scale(1)';
            }}>
              Enroll Now
            </button>
            <button style={{
              backgroundColor: 'transparent',
              border: '2px solid white',
              padding: '15px 40px',
              fontSize: '1.1rem',
              fontWeight: '600',
              borderRadius: '8px',
              color: 'white',
              cursor: 'pointer',
              transition: 'all 0.3s ease'
            }}
            onMouseOver={(e) => {
              e.target.style.backgroundColor = 'white';
              e.target.style.color = '#58A95B';
            }}
            onMouseOut={(e) => {
              e.target.style.backgroundColor = 'transparent';
              e.target.style.color = 'white';
            }}>
              Download Catalog
            </button>
          </div>
        </div>
      </section>

      <style>{`
        @keyframes fadeInUp {
          from {
            opacity: 0;
            transform: translateY(30px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }

        @keyframes float {
          0%, 100% {
            transform: translateY(0);
          }
          50% {
            transform: translateY(-20px);
          }
        }

        @keyframes pulse {
          0%, 100% {
            opacity: 0.5;
          }
          50% {
            opacity: 0.8;
          }
        }

        * {
          box-sizing: border-box;
        }

        button {
          font-family: inherit;
        }

        @media (max-width: 768px) {
          section {
            padding: 60px 15px !important;
          }
        }
      `}</style>
    </div>
  );
};

export default Certifications;